import { Button, Divider, Group, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { IconCode, IconEye, IconSettings, IconShare } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { showNotification } from '@mantine/notifications';

import { useGPTStore } from '@/store';
import { ModalWidget } from '@/components/Widget/Embed/Code';
import ShareModal from '@/components/Widget/Embed/Modal/ShareModal';

interface DisplayMethodProps {
  /** 是否显示"自定义"按钮（数字人页面需要跳转到小部件嵌入页） */
  showCustomize?: boolean;
}

const DisplayMethod = ({ showCustomize = false }: DisplayMethodProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const robotInfo = useGPTStore((state) => state.robotInfo);
  const [openedWidget, { close: closeWidget, open: openWidget }] = useDisclosure(false);
  const [openedShare, { open: openShareModal, close: closeShareModal }] = useDisclosure(false);

  const handleCustomizeWidget = () => {
    showNotification({
      title: t('widget.notification.navigate_to_widget_embed.title'),
      message: t('widget.notification.navigate_to_widget_embed.message'),
      color: 'violet',
      autoClose: 3000,
    });

    setTimeout(() => {
      navigate(`/bot/${robotInfo?.id}/widget-embed`);
    }, 3000);
  };

  const openPreview = () => {
    const currentUrl = new URL(window.location.href);
    const pathSegments = currentUrl.pathname.split('/');
    const botId = pathSegments[2];
    if (botId) {
      const newUrl = new URL(`/widget-preview?id=${botId}`, currentUrl.origin);
      window.open(newUrl.toString(), '_blank', 'noopener,noreferrer');
    }
  };

  const handlePreviewShare = () => {
    const shareUrl = import.meta.env.VITE_BASE_HOST + `/widget-page/${robotInfo?.id || ''}`;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const shareUrl = import.meta.env.VITE_BASE_HOST + `/widget-page/${robotInfo?.id || ''}`;

  return (
    <>
      <div className="bg-white rounded-xl p-6" style={{ boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50)' }}>
        <Title order={4} mb={16}>
          {t('widget.display_method.title')}
        </Title>

        <div className="space-y-6">
          {/* 小部件 */}
          <div>
            <Text size="sm" fw={600} mb={8}>
              {t('widget.display_method.widget.title')}
            </Text>
            <Text size="xs" c="gray.6" mb={12}>
              {t('widget.display_method.widget.description')}
            </Text>
            <Group gap={12}>
              {showCustomize && (
                <Button variant="outline" leftSection={<IconSettings size={16} />} onClick={handleCustomizeWidget}>
                  {t('widget.display_method.widget.customize')}
                </Button>
              )}
              <Button variant="outline" leftSection={<IconCode size={16} />} onClick={openWidget}>
                {t('widget.display_method.widget.embed_website')}
              </Button>
              <Button variant="outline" leftSection={<IconEye size={16} />} onClick={openPreview}>
                {t('widget.display_method.widget.preview')}
              </Button>
            </Group>
          </div>

          <Divider />

          {/* 分享 */}
          <div>
            <Text size="sm" fw={600} mb={8}>
              {t('widget.display_method.share.title')}
            </Text>
            <Text size="xs" c="gray.6" mb={12}>
              {t('widget.display_method.share.description')}
            </Text>
            <Group gap={12}>
              <Button variant="outline" leftSection={<IconShare size={16} />} onClick={openShareModal}>
                {t('widget.display_method.share.link_and_qr')}
              </Button>
              <Button variant="outline" leftSection={<IconEye size={16} />} onClick={handlePreviewShare}>
                {t('widget.display_method.share.preview')}
              </Button>
            </Group>
          </div>
        </div>
      </div>

      <ModalWidget opened={openedWidget} close={closeWidget} hideSetting />
      <ShareModal opened={openedShare} onClose={closeShareModal} url={shareUrl} id={robotInfo?.id || ''} />
    </>
  );
};

export default DisplayMethod;
